/**
 * Trace-waterfall layout math — pure and DOM-free so it is trivially
 * unit-testable (test/trace-layout.test.ts) and safe to call from a computed.
 *
 * Given a trace's spans it produces, for each span:
 * - `depth`      — nesting level from its root chain (indents the label),
 * - `offsetFrac` — where the bar starts on the shared 0..1 time track,
 * - `widthFrac`  — how wide the bar is (floored so instant spans stay visible),
 * - `order`      — the DFS pre-order row index (root, then children by start),
 *
 * so the component only positions `<span>`s with percentages.
 */

import type { TraceSpan } from '../api.js';
import { formatMs } from './format-metric.js';

export interface LaidOutSpan {
  span: TraceSpan;
  /** Nesting depth: 0 for a root (or an orphan whose parent isn't in the trace). */
  depth: number;
  /** Bar start as a fraction of the whole trace duration, clamped to 0..1. */
  offsetFrac: number;
  /** Bar width as a fraction of the whole trace, floored at MIN_VISIBLE_FRAC. */
  widthFrac: number;
  /** Row index in DFS pre-order (parents above their children, siblings by start). */
  order: number;
}

/** A near-instant span still renders ~0.4% of the track so the eye can find it. */
export const MIN_VISIBLE_FRAC = 0.004;

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/**
 * Lay out a trace's spans for the waterfall.
 *
 * - Orphans (a `parentSpanId` that doesn't resolve to a span in this trace, or a
 *   self-parent) are treated as roots.
 * - A zero-duration trace (single instant span) never divides by zero: the
 *   denominator is floored at 1ns.
 * - Defensive: spans stuck in a parent cycle (unreachable from any root) are
 *   appended at depth 0 rather than silently dropped.
 */
export function layoutSpans(spans: TraceSpan[]): LaidOutSpan[] {
  if (spans.length === 0) return [];

  let traceStartNs = Infinity;
  let traceEndNs = -Infinity;
  for (const s of spans) {
    if (s.startNs < traceStartNs) traceStartNs = s.startNs;
    if (s.endNs > traceEndNs) traceEndNs = s.endNs;
  }
  const traceDurationNs = Math.max(1, traceEndNs - traceStartNs);

  // Build the parent → children map; unresolvable parents make a span a root.
  const byId = new Map<string, TraceSpan>();
  for (const s of spans) byId.set(s.spanId, s);
  const children = new Map<string, TraceSpan[]>();
  const roots: TraceSpan[] = [];
  for (const s of spans) {
    const parent = s.parentSpanId;
    if (parent && parent !== s.spanId && byId.has(parent)) {
      const list = children.get(parent);
      if (list) list.push(s);
      else children.set(parent, [s]);
    } else {
      roots.push(s);
    }
  }
  const byStart = (a: TraceSpan, b: TraceSpan) => a.startNs - b.startNs;
  roots.sort(byStart);
  for (const list of children.values()) list.sort(byStart);

  const out: LaidOutSpan[] = [];
  const visited = new Set<string>();

  function place(s: TraceSpan, depth: number) {
    if (visited.has(s.spanId)) return; // duplicate id / cycle guard
    visited.add(s.spanId);
    out.push({
      span: s,
      depth,
      offsetFrac: clamp01((s.startNs - traceStartNs) / traceDurationNs),
      widthFrac: Math.max(MIN_VISIBLE_FRAC, clamp01(s.durationNs / traceDurationNs)),
      order: out.length,
    });
    const kids = children.get(s.spanId);
    if (kids) for (const k of kids) place(k, depth + 1);
  }

  for (const r of roots) place(r, 0);

  // Spans trapped in a parent cycle are unreachable from any root — surface
  // them at depth 0 (in start order) instead of losing them.
  const leftovers = spans.filter((s) => !visited.has(s.spanId)).sort(byStart);
  for (const s of leftovers) place(s, 0);

  return out;
}

/**
 * Friendly duration for a span/trace given NANOSECONDS ("850 µs", "12.3 ms",
 * "1.5 s"). Delegates to the shared ms formatter so trace durations read the
 * same as every duration on the metrics surface.
 */
export function formatDurationNs(ns: number): string {
  return formatMs(ns / 1e6);
}
